import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUsers, migrateLocalStorageToSupabase, verifyUserPasscode, setUserPasscode } from '../utils/supabaseDataManager';
import PasscodePrompt from '../components/PasscodePrompt';
import './UserSelect.css';

function UserSelect() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasscodePrompt, setShowPasscodePrompt] = useState(false);
  const [selectedUsername, setSelectedUsername] = useState(null);
  const [passcodeMode, setPasscodeMode] = useState('verify');

  useEffect(() => {
    async function loadUsers() {
      try {
        // Check if we need to migrate from localStorage
        const usersStr = localStorage.getItem('appUsers');
        const hasLocalUsers = usersStr && JSON.parse(usersStr).length > 0;

        if (hasLocalUsers) {
          console.log('Found localStorage users, migrating to Supabase...');
          const localUsers = JSON.parse(usersStr);

          // Migrate each user
          for (const user of localUsers) {
            await migrateLocalStorageToSupabase(user.username);
          }

          console.log('Migration complete!');
        }

        // Load users from Supabase
        const supabaseUsers = await getAllUsers();

        if (supabaseUsers && supabaseUsers.length > 0) {
          setUsers(supabaseUsers);

          // Auto-select if only one user and no currentUser set
          const currentUser = localStorage.getItem('currentUser');
          if (!currentUser && supabaseUsers.length === 1) {
            localStorage.setItem('currentUser', supabaseUsers[0].username);
            navigate('/');
          }
        }
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    }

    loadUsers();
  }, [navigate]);

  const handleSelectUser = (username) => {
    setSelectedUsername(username);
    setShowPasscodePrompt(true);
    setPasscodeMode('verify');
  };

  const handlePasscodeSuccess = async (passcode) => {
    const result = await verifyUserPasscode(selectedUsername, passcode);

    if (result.success && result.needsSetup) {
      // User doesn't have a passcode set yet, allow login anyway (backwards compatibility)
      setShowPasscodePrompt(false);
      localStorage.setItem('currentUser', selectedUsername);
      // Trigger storage event for immediate update
      window.dispatchEvent(new Event('storage'));
      // Wait for state to propagate before navigating
      setTimeout(() => navigate('/'), 600);
    } else if (result.success) {
      // Correct passcode, log in
      setShowPasscodePrompt(false);
      localStorage.setItem('currentUser', selectedUsername);
      // Trigger storage event for immediate update
      window.dispatchEvent(new Event('storage'));
      // Wait for state to propagate before navigating
      setTimeout(() => navigate('/'), 600);
    } else {
      // Incorrect passcode
      alert(result.error?.message || 'Incorrect passcode. Please try again.');
      setShowPasscodePrompt(false);
      setSelectedUsername(null);
    }
  };

  const handlePasscodeSetup = async (passcode) => {
    const result = await setUserPasscode(selectedUsername, passcode);

    if (result.success) {
      // Passcode set successfully, log in
      setShowPasscodePrompt(false);
      localStorage.setItem('currentUser', selectedUsername);
      // Trigger storage event for immediate update
      window.dispatchEvent(new Event('storage'));
      // Wait for state to propagate before navigating
      setTimeout(() => navigate('/'), 600);
    } else {
      alert('Failed to set passcode. Please try again.');
      setShowPasscodePrompt(false);
      setSelectedUsername(null);
    }
  };

  const handlePasscodeCancel = () => {
    setShowPasscodePrompt(false);
    setSelectedUsername(null);
    setPasscodeMode('verify');
  };

  const handleAddUser = () => {
    const password = prompt("This is a one-time user registration code to keep strangers off. Just type Jesse's last name:");

    if (password === 'Gossman') {
      navigate('/onboarding');
    } else if (password !== null) {
      // User entered something but it's wrong
      alert('Incorrect password. Registration not allowed.');
    }
    // If password is null, user clicked cancel - do nothing
  };

  if (loading) {
    return (
      <div className="user-select-screen">
        <div className="user-select-container">
          <div className="app-logo">
            <h1 className="logo-text">💪 Functional Strength</h1>
            <p className="logo-subtitle">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-select-screen">
      <div className="user-select-container">
        <div className="app-logo">
          <h1 className="logo-text">💪 Functional Strength</h1>
          <p className="logo-subtitle">AI-Powered Personal Training</p>
        </div>

        <div className="user-select-card">
          <h2 className="select-title">Who's working out today?</h2>

          <div className="user-list">
            {users.map(user => (
              <button
                key={user.username}
                className="user-button"
                onClick={() => handleSelectUser(user.username)}
              >
                <div className="user-avatar">{user.username.charAt(0).toUpperCase()}</div>
                <div className="user-info">
                  <span className="user-name">{user.display_name || user.username}</span>
                  <span className="user-meta">{user.primary_goal || 'Building strength'}</span>
                </div>
                <span className="user-arrow">→</span>
              </button>
            ))}
          </div>

          <button className="add-user-button" onClick={handleAddUser}>
            <span className="add-icon">+</span>
            Add New User
          </button>
        </div>

        <div className="welcome-features">
          <h3 className="features-title">What you'll get:</h3>
          <ul className="features-list">
            <li>🧠 AI-designed weekly workout plans</li>
            <li>📊 Progress tracking & measurements</li>
            <li>💪 Exercise guides for Planet Fitness & home</li>
            <li>🎯 Personalized progressive overload</li>
            <li>📱 Works offline, installs like an app</li>
          </ul>
        </div>
      </div>

      {showPasscodePrompt && selectedUsername && (
        <PasscodePrompt
          username={selectedUsername}
          mode={passcodeMode}
          onSuccess={passcodeMode === 'setup' ? handlePasscodeSetup : handlePasscodeSuccess}
          onCancel={handlePasscodeCancel}
        />
      )}
    </div>
  );
}

export default UserSelect;
